import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, FileText, Scale, Award, GraduationCap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const PeritacionMedica = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const servicios = [
    {
      icono: FileText,
      titulo: 'Informes Periciales',
      tituloEn: 'Expert Reports',
      tituloFr: 'Rapports d\'expertise',
      tituloPt: 'Relatórios Periciais',
      descripcion: 'Elaboración de informes médico-periciales rigurosos para procedimientos judiciales y extrajudiciales.',
      descripcionEn: 'Preparation of rigorous medical-legal reports for judicial and extrajudicial proceedings.',
      descripcionFr: 'Élaboration de rapports médico-légaux rigoureux pour les procédures judiciaires et extrajudiciaires.',
      descripcionPt: 'Elaboração de relatórios médico-periciais rigorosos para procedimentos judiciais e extrajudiciais.',
    },
    {
      icono: Scale,
      titulo: 'Valoración del Daño Corporal',
      tituloEn: 'Bodily Damage Assessment',
      tituloFr: 'Évaluation du Dommage Corporel',
      tituloPt: 'Avaliação do Dano Corporal',
      descripcion: 'Evaluación conforme al Baremo vigente. Secuelas estéticas y funcionales.',
      descripcionEn: 'Assessment according to current regulations. Aesthetic and functional sequelae.',
      descripcionFr: 'Évaluation conforme au barème en vigueur. Séquelles esthétiques et fonctionnelles.',
      descripcionPt: 'Avaliação conforme o Baremo vigente. Sequelas estéticas e funcionais.',
    },
  ];

  const ambitos = {
    es: ['Accidentes de tráfico', 'Accidentes laborales', 'Negligencias médicas', 'Agresiones', 'Reclamaciones de seguros', 'Incapacidades'],
    en: ['Traffic accidents', 'Work accidents', 'Medical negligence', 'Assaults', 'Insurance claims', 'Disabilities'],
    fr: ['Accidents de la route', 'Accidents du travail', 'Négligences médicales', 'Agressions', 'Réclamations d\'assurance', 'Incapacités'],
    pt: ['Acidentes de trânsito', 'Acidentes de trabalho', 'Negligências médicas', 'Agressões', 'Reclamações de seguros', 'Incapacidades'],
  };

  const colaboracion = {
    es: ['Despachos de abogados', 'Compañías aseguradoras', 'Juzgados y tribunales', 'Mutuas de accidentes'],
    en: ['Law firms', 'Insurance companies', 'Courts and tribunals', 'Accident mutual funds'],
    fr: ['Cabinets d\'avocats', 'Compagnies d\'assurance', 'Tribunaux et cours', 'Mutuelles d\'accidents'],
    pt: ['Escritórios de advocacia', 'Companhias de seguros', 'Tribunais', 'Mútuas de acidentes'],
  };

  const getLocalizedText = (obj, key) => {
    const langKey = key + lang.charAt(0).toUpperCase() + lang.slice(1);
    return obj[langKey] || obj[key];
  };

  const texts = {
    es: {
      eyebrow: 'Cirugía Reparadora y Pericial',
      title: 'Peritación Médica y Valoración del Daño Corporal',
      description: 'Expertise médico-legal en cirugía plástica. Informes periciales para procedimientos judiciales y extrajudiciales. Valoración del daño corporal conforme al Baremo vigente.',
      requestInfo: 'Solicitar Información',
      callNow: 'Llamar Ahora',
      services: 'Servicios',
      ourExpertise: 'Nuestra expertise',
      areasTitle: 'Ámbitos de Actuación',
      caseTypes: 'Tipos de casos',
      collaboration: 'Colaboración',
      workWith: 'Trabajamos con',
      needReport: '¿Necesita un informe pericial?',
      contactUs: 'Contáctenos para valorar su caso. Confidencialidad garantizada.',
      contact: 'Contactar',
      credentials: 'Acreditación',
      credentialTitle: 'Experto Universitario en Valoración del Daño Corporal',
      credentialDesc: 'Instituto de Ciencias Médicas y Jurídicas de Granada, con el reconocimiento oficial de la Junta de Andalucía.',
    },
    en: {
      eyebrow: 'Reconstructive and Expert Surgery',
      title: 'Medical Expertise and Bodily Damage Assessment',
      description: 'Medical-legal expertise in plastic surgery. Expert reports for judicial and extrajudicial proceedings. Bodily damage assessment according to current regulations.',
      requestInfo: 'Request Information',
      callNow: 'Call Now',
      services: 'Services',
      ourExpertise: 'Our expertise',
      areasTitle: 'Areas of Practice',
      caseTypes: 'Case types',
      collaboration: 'Collaboration',
      workWith: 'We work with',
      needReport: 'Need an expert report?',
      contactUs: 'Contact us to assess your case. Confidentiality guaranteed.',
      contact: 'Contact',
      credentials: 'Accreditation',
      credentialTitle: 'University Expert in Bodily Damage Assessment',
      credentialDesc: 'Institute of Medical and Legal Sciences of Granada, with official recognition from the Regional Government of Andalusia.',
    },
    fr: {
      eyebrow: 'Chirurgie Réparatrice et Expertale',
      title: 'Expertise Médicale et Évaluation du Dommage Corporel',
      description: 'Expertise médico-légale en chirurgie plastique. Rapports d\'expertise pour les procédures judiciaires et extrajudiciaires. Évaluation du dommage corporel selon le barème en vigueur.',
      requestInfo: 'Demander des Informations',
      callNow: 'Appeler Maintenant',
      services: 'Services',
      ourExpertise: 'Notre expertise',
      areasTitle: 'Domaines d\'Intervention',
      caseTypes: 'Types de cas',
      collaboration: 'Collaboration',
      workWith: 'Nous travaillons avec',
      needReport: 'Besoin d\'un rapport d\'expertise?',
      contactUs: 'Contactez-nous pour évaluer votre cas. Confidentialité garantie.',
      contact: 'Contacter',
      credentials: 'Accréditation',
      credentialTitle: 'Expert Universitaire en Évaluation du Dommage Corporel',
      credentialDesc: 'Institut des Sciences Médicales et Juridiques de Grenade, avec reconnaissance officielle du Gouvernement d\'Andalousie.',
    },
    pt: {
      eyebrow: 'Cirurgia Reparadora e Pericial',
      title: 'Perícia Médica e Avaliação do Dano Corporal',
      description: 'Expertise médico-legal em cirurgia plástica. Relatórios periciais para procedimentos judiciais e extrajudiciais. Avaliação do dano corporal conforme o Baremo vigente.',
      requestInfo: 'Solicitar Informação',
      callNow: 'Ligar Agora',
      services: 'Serviços',
      ourExpertise: 'Nossa expertise',
      areasTitle: 'Áreas de Atuação',
      caseTypes: 'Tipos de casos',
      collaboration: 'Colaboração',
      workWith: 'Trabalhamos com',
      needReport: 'Precisa de um relatório pericial?',
      contactUs: 'Entre em contato para avaliar seu caso. Confidencialidade garantida.',
      contact: 'Contatar',
      credentials: 'Credenciamento',
      credentialTitle: 'Especialista Universitário em Avaliação do Dano Corporal',
      credentialDesc: 'Instituto de Ciências Médicas e Jurídicas de Granada, com reconhecimento oficial do Governo da Andaluzia.',
    },
  };

  const txt = texts[lang] || texts.es;
  const currentAmbitos = ambitos[lang] || ambitos.es;
  const currentColaboracion = colaboracion[lang] || colaboracion.es;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{txt.eyebrow}</p>
              <h1 className="font-serif text-4xl md:text-5xl text-[#1C1C1A] mb-6 leading-tight">
                {txt.title}
              </h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-8">
                {txt.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contacto"
                  className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors"
                >
                  {txt.requestInfo}
                  <ArrowRight size={16} />
                </Link>
                <a 
                  href="tel:+34952764547"
                  className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors"
                >
                  <Phone size={16} />
                  {txt.callNow}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img 
                src="https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Peritación Médica"
                className="w-full h-auto"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Credencial - NUEVA SECCIÓN */}
      <section className="py-16 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <div className="bg-white p-8 md:p-12 border-l-4 border-[#B8975A] shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-[#B8975A]/10 flex items-center justify-center flex-shrink-0 rounded-full">
                  <GraduationCap size={32} className="text-[#B8975A]" />
                </div>
                <div>
                  <p className="text-sm tracking-[0.2em] uppercase text-[#B8975A] mb-2">{txt.credentials}</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#1C1C1A] mb-4">
                    {txt.credentialTitle}
                  </h3>
                  <p className="text-[#4A4845] text-lg leading-relaxed">
                    {txt.credentialDesc}
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Servicios */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{txt.services}</p>
            <h2 className="font-serif text-4xl text-white">{txt.ourExpertise}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {servicios.map((servicio, index) => (
              <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                <div className="p-8 border border-[#B8975A]/30 hover:border-[#B8975A] transition-colors text-center h-full">
                  <servicio.icono size={40} className="text-[#B8975A] mx-auto mb-6" />
                  <h3 className="font-serif text-2xl text-white mb-4">
                    {lang === 'en' ? servicio.tituloEn : lang === 'fr' ? servicio.tituloFr : lang === 'pt' ? servicio.tituloPt : servicio.titulo}
                  </h3>
                  <p className="text-white/70">
                    {lang === 'en' ? servicio.descripcionEn : lang === 'fr' ? servicio.descripcionFr : lang === 'pt' ? servicio.descripcionPt : servicio.descripcion}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Ámbitos */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-16">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{txt.areasTitle}</p>
              <h2 className="font-serif text-3xl text-[#1C1C1A] mb-8">{txt.caseTypes}</h2>
              <div className="space-y-4">
                {currentAmbitos.map((ambito, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-white">
                    <div className="w-6 h-6 bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-[#4A4845]">{ambito}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{txt.collaboration}</p>
              <h2 className="font-serif text-3xl text-[#1C1C1A] mb-8">{txt.workWith}</h2>
              <div className="space-y-4">
                {currentColaboracion.map((colab, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-white">
                    <div className="w-6 h-6 bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-[#4A4845]">{colab}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">
              {txt.needReport}
            </h2>
            <p className="text-[#4A4845] mb-10">
              {txt.contactUs}
            </p>
            <Link 
              to="/contacto"
              className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors"
            >
              {txt.contact}
              <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default PeritacionMedica;
